import React, { useState, useEffect, useRef, useCallback } from 'react';
import { V, Haptic } from '../utils/theme';
import { Icons } from '../components/Icons';
import { Btn, Card, Skeleton, SuccessToastCtrl } from '../components/ui';
import { Eyebrow, IconTile, EmptyState, Notice, LinkBtn } from '../components/kit';
import { useOrg, orgApi, orgErr, errorMessage, onOrgPing, personName, fmtWhen, fmtDay, shortScope } from '../utils/org';
import { useVoiceRecorder, recorderSupported, checkVideoFile, MediaContent, ReplyQuote, FormCheckSheet } from '../components/InboxMedia';
import { runMediaJob } from '../utils/mediaUpload';
import { useGymExercises } from '../components/GymExercises';
import { RECORD_MAX_SEC, fmtDuration, mediaLabel, mediaSpec, mediaErrorText, mergeMedia, mediaExpired, replyPreview, canReplyTo } from '../utils/messageMedia';

// ─── Gym inbox: announcements from the gym + one message thread per coach ───
// Separate from social Messages on purpose (different API, different rules):
// nothing in src/tabs/social is touched. Composer sends on button tap only
// (Enter inserts a newline — iOS keyboard), 2000 chars, optimistic append.
// Threads also carry voice notes, videos and form checks (components/InboxMedia.jsx)
// and replies that quote an earlier message.
const POLL_MS = 30000;
const MAX_LEN = 2000;

export function OrgInbox({ s, d }) {
  const { org, unread, setUnread, refreshOrg } = useOrg();
  const email = s.profile?.email;
  const [announcements, setAnnouncements] = useState(null);
  const [threads, setThreads] = useState(null);
  const [err, setErr] = useState(null);
  const [openThread, setOpenThread] = useState(null); // thread id
  const [openAnn, setOpenAnn] = useState(null);       // announcement id
  const [offline, setOffline] = useState(false);
  const gymEx = useGymExercises(); // the gym's exercises are offered in the form-check picker too

  const load = useCallback(async () => {
    try {
      const [a, t] = await Promise.all([orgApi('list_announcements', { limit: 30 }), orgApi('threads')]);
      setAnnouncements(sortAnn(a?.announcements || []));
      setThreads(t?.threads || []);
      setErr(null); setOffline(false);
    } catch (e) {
      const er = orgErr(e);
      if (er.status === 0) setOffline(true); else setErr(er);
      // Functional setters: only replace the initial skeleton, never a loaded list
      setAnnouncements(a => a ?? []);
      setThreads(t => t ?? []);
    }
  }, []);

  // Initial load + realtime ping + 30 s poll while visible
  useEffect(() => {
    load();
    const off = onOrgPing(() => load());
    let timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    const vis = () => { clearInterval(timer); if (document.visibilityState === 'visible') load(); timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS); };
    document.addEventListener('visibilitychange', vis);
    return () => { off(); clearInterval(timer); document.removeEventListener('visibilitychange', vis); };
  }, [load]);

  const coaches = org?.coaches?.length ? org.coaches : (org?.coach ? [org.coach] : []);
  const myThreads = (threads || []).filter(t => t.mine !== false);
  // A coach with no thread yet still gets a row so the member can start the conversation
  const rows = [...myThreads.map(t => ({ kind: 'thread', id: t.id, who: t.coach?.email === email ? t.client : t.coach, unread: t.unread, preview: t.last_preview, at: t.last_message_at, active: t.active })),
    ...coaches.filter(c => !myThreads.some(t => t.coach?.email === c.email)).map(c => ({ kind: 'new', id: 'new:' + c.email, who: c, unread: 0, preview: 'Say hi to your coach', at: null, active: true }))];

  const openAnnouncement = async (a) => {
    setOpenAnn(a.id === openAnn ? null : a.id);
    if (!a.read_at) {
      setAnnouncements(list => list.map(x => x.id === a.id ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread(u => ({ ...u, announcements: Math.max(0, (u?.announcements || 0) - 1) }));
      try { await orgApi('ack_announcement', { id: a.id }); } catch {}
    }
  };

  if (openThread) {
    const row = rows.find(r => r.id === openThread);
    return <ThreadView key={openThread} row={row} email={email} exerciseNames={[...gymEx.catalog, ...(s.exercises || [])].map(e => e.name)} onBack={() => { setOpenThread(null); load(); refreshOrg(); }} onRead={(n) => setUnread(u => ({ ...u, messages: Math.max(0, (u?.messages || 0) - n) }))} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Back row lives here (not in App.jsx) so the thread view above can fill --content-h exactly */}
      <button onClick={() => d?.({ type: "TAB", tab: "home" })} style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 5, alignSelf: "flex-start",
        paddingTop: 8, paddingBottom: 0, paddingLeft: 0, paddingRight: 0, marginBottom: -4, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
        {Icons.chevLeft({ size: 18, color: V.accent })}
        <span style={{ fontSize: 14, color: V.accent, fontWeight: 600 }}>Home</span>
      </button>
      <div>
        <Eyebrow color={V.accent} style={{ marginBottom: 6 }}>{org?.org?.name || 'Your gym'}</Eyebrow>
        <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", color: V.text, lineHeight: 1.15 }}>Inbox</div>
        <div style={{ fontSize: 13, color: V.text2, marginTop: 4 }}>Announcements and messages from your coach</div>
      </div>
      {offline && <Notice tone="warn">Offline — showing what was loaded last</Notice>}
      {err && <Notice tone="error">{errorMessage(err)}</Notice>}

      <section aria-label="Announcements">
        <Eyebrow style={{ marginBottom: 8, paddingLeft: 2 }}>Announcements{unread?.announcements > 0 ? ` · ${unread.announcements} new` : ''}</Eyebrow>
        {announcements === null ? <Card><Skeleton rows={2} /></Card>
          : announcements.length === 0 ? <EmptyState icon={Icons.bell} title="No announcements yet">Your gym's news will show up here.</EmptyState>
          : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {announcements.map(a => {
              const pinned = a.pinned_until && new Date(a.pinned_until) > new Date();
              const isOpen = openAnn === a.id;
              return (
                <Card key={a.id} onClick={() => openAnnouncement(a)} style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, borderColor: !a.read_at ? `${V.accent}40` : undefined }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <IconTile icon={pinned ? Icons.zap : Icons.bell} color={pinned ? V.warn : V.accent} size={32} iconSize={15} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {!a.read_at && <span aria-label="Unread" style={{ width: 7, height: 7, borderRadius: 4, background: V.accent, flexShrink: 0 }} />}
                        <div style={{ fontSize: 14, fontWeight: 700, color: V.text, flex: 1, minWidth: 0, whiteSpace: isOpen ? "normal" : "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title}</div>
                        {pinned && <span style={{ fontSize: 9, fontWeight: 700, color: V.warn, letterSpacing: ".08em", textTransform: "uppercase" }}>Pinned</span>}
                      </div>
                      <div style={{ fontSize: 12, color: V.text2, marginTop: 4, lineHeight: 1.5, whiteSpace: isOpen ? "pre-wrap" : "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.body}</div>
                      <div style={{ fontSize: 10, color: V.text3, marginTop: 6 }}>{personName(a.author)} · {fmtDay(a.created_at)}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>}
      </section>

      <section aria-label="Messages">
        <Eyebrow style={{ marginBottom: 8, paddingLeft: 2 }}>Messages{unread?.messages > 0 ? ` · ${unread.messages} unread` : ''}</Eyebrow>
        {threads === null ? <Card><Skeleton rows={2} /></Card>
          : rows.length === 0 ? <EmptyState icon={Icons.users} title="No coach assigned yet">Once a coach is assigned to you, you can message them here.</EmptyState>
          : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map(r => (
              <Card key={r.id} onClick={() => setOpenThread(r.id)} style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, borderColor: r.unread > 0 ? `${V.accent}40` : undefined }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Avatar who={r.who} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: V.text }}>{personName(r.who)}<span style={{ fontSize: 10, color: V.text3, fontWeight: 600, marginLeft: 6 }}>Coach</span></div>
                      {r.at && <div style={{ fontSize: 10, color: V.text3, fontFamily: V.mono, flexShrink: 0 }}>{fmtWhen(r.at)}</div>}
                    </div>
                    <div style={{ fontSize: 12, color: r.unread > 0 ? V.text : V.text3, fontWeight: r.unread > 0 ? 600 : 400, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.preview || 'No messages yet'}</div>
                  </div>
                  {r.unread > 0 && <div style={{ minWidth: 18, height: 18, borderRadius: 9, background: V.danger, display: "flex", alignItems: "center", justifyContent: "center", paddingLeft: 5, paddingRight: 5 }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: "#fff", fontFamily: V.mono }}>{r.unread > 99 ? '99+' : r.unread}</span></div>}
                  {Icons.chevRight({ size: 16, color: V.text3 })}
                </div>
              </Card>
            ))}
          </div>}
      </section>
    </div>
  );
}

function sortAnn(list) {
  const now = Date.now();
  const pinned = (a) => a.pinned_until && new Date(a.pinned_until).getTime() > now ? 1 : 0;
  return [...list].sort((a, b) => (pinned(b) - pinned(a)) || (new Date(b.created_at) - new Date(a.created_at)));
}

const Avatar = ({ who, size = 40 }) => {
  const name = personName(who);
  return who?.avatar_url
    ? <img src={who.avatar_url} alt="" style={{ width: size, height: size, borderRadius: size * 0.32, objectFit: "cover", flexShrink: 0 }} />
    : <div aria-hidden style={{ width: size, height: size, borderRadius: size * 0.32, background: `linear-gradient(135deg,${V.accent},${V.accent2})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ fontSize: size * 0.42, fontWeight: 800, color: V.onAccent }}>{name[0]?.toUpperCase() || '?'}</span>
      </div>;
};

// ─── One thread ───
let _tmpSeq = 0;
function ThreadView({ row, email, exerciseNames, onBack, onRead }) {
  const [threadId, setThreadId] = useState(row?.kind === 'thread' ? row.id : null);
  const [msgs, setMsgs] = useState(threadId ? null : []);
  const [nextBefore, setNextBefore] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [replyTo, setReplyTo] = useState(null);      // the message being replied to
  const [attachOpen, setAttachOpen] = useState(false);
  const [formCheck, setFormCheck] = useState(false);
  const [mediaNote, setMediaNote] = useState(null);   // a refused file / recorder message
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const textRef = useRef(null);
  const recordInput = useRef(null), chooseInput = useRef(null);
  const jobs = useRef({});        // temp message id → upload job
  const threadRef = useRef(threadId);
  threadRef.current = threadId;
  const rec = useVoiceRecorder();
  const canRecord = recorderSupported();
  const who = row?.who;
  const otherName = personName(who);
  const readOnly = row?.active === false;
  // `onRead` is an inline prop from the inbox; keep it in a ref so it never
  // re-triggers the fetch effect below (a new function per parent render).
  const onReadRef = useRef(onRead);
  useEffect(() => { onReadRef.current = onRead; });

  // mark_read {thread_id, up_to_id} whenever the thread is viewed with unread
  // messages from the other side; local copies are marked so it runs once.
  const markRead = useCallback(async (list, tid) => {
    const unread = list.filter(m => m.sender_email !== email && !m.read_at && !String(m.id).startsWith('tmp-'));
    if (!tid || !unread.length) return;
    const last = unread[unread.length - 1];
    try {
      await orgApi('mark_read', { thread_id: tid, up_to_id: last.id });
      const now = new Date().toISOString();
      setMsgs(cur => (cur || []).map(m => m.sender_email !== email && !m.read_at ? { ...m, read_at: now } : m));
      onReadRef.current?.(unread.length);
    } catch {}
  }, [email]);

  const fetchMessages = useCallback(async (tid, before) => {
    const r = await orgApi('thread_messages', { thread_id: tid, limit: 50, ...(before ? { before } : {}) });
    return { list: r?.messages || [], next: r?.next_before || null };
  }, []);

  useEffect(() => {
    if (!threadId) return;
    let dead = false;
    (async () => {
      try {
        const { list, next } = await fetchMessages(threadId);
        if (dead) return;
        setMsgs(cur => merge(cur, list)); setNextBefore(next); setErr(null);
        markRead(list, threadId);
      } catch (e) { if (!dead) { setErr(orgErr(e)); setMsgs(m => m || []); } }
    })();
    const off = onOrgPing(async () => {
      try { const { list } = await fetchMessages(threadId); if (!dead) { setMsgs(cur => merge(cur, list)); markRead(list, threadId); } } catch {}
    });
    const timer = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      try { const { list } = await fetchMessages(threadId); if (!dead) { setMsgs(cur => merge(cur, list)); markRead(list, threadId); } } catch {}
    }, POLL_MS);
    return () => { dead = true; off(); clearInterval(timer); };
  }, [threadId, fetchMessages, markRead]);

  // Local previews of uploads are object URLs; free them when the thread closes.
  useEffect(() => () => { for (const j of Object.values(jobs.current)) { if (j.localUrl) { try { URL.revokeObjectURL(j.localUrl); } catch {} } } }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView?.({ block: "end" }); }, [msgs?.length]);

  const loadMore = async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try { const { list, next } = await fetchMessages(threadId, nextBefore); setMsgs(cur => merge(list, cur || [])); setNextBefore(next); } catch {}
    setLoadingMore(false);
  };

  // A player failed to load: most likely the signed link expired. Mark it stale and re-read
  // the thread so the merge takes a fresh link. One retry per clip per 10 minutes (unless
  // the link has expired again): a clip this browser cannot decode would otherwise loop,
  // since every read signs a new URL. After that the clip offers to open the file instead.
  const retried = useRef({});
  const refetching = useRef(null);
  const onMediaError = useCallback(async (m) => {
    const tid = threadRef.current, id = m.media && m.media.id;
    if (!tid || !id) return;
    const last = retried.current[id];
    if (last && Date.now() - last < 600000 && !mediaExpired(m.media)) {
      setMsgs(cur => (cur || []).map(x => x.id === m.id && x.media ? { ...x, media: { ...x.media, _broken: true } } : x));
      return;
    }
    retried.current[id] = Date.now();
    setMsgs(cur => (cur || []).map(x => x.id === m.id && x.media ? { ...x, media: { ...x.media, _fetched_at: 0, _broken: false } } : x));
    if (refetching.current) return;
    refetching.current = fetchMessages(tid).then(({ list }) => setMsgs(cur => merge(cur, list))).catch(() => {}).finally(() => { refetching.current = null; });
  }, [fetchMessages]);

  const startReply = (m) => {
    if (readOnly || !canReplyTo(m)) return;
    Haptic.light();
    setReplyTo(m);
    setTimeout(() => textRef.current?.focus(), 30);
  };
  const jumpTo = (id) => {
    const el = listRef.current?.querySelector(`[data-msg-id="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.animate?.([{ opacity: .35 }, { opacity: 1 }], { duration: 700 });
  };

  const send = async () => {
    const body = text.trim().slice(0, MAX_LEN);
    if (!body || sending) return;
    Haptic.light();
    setSending(true); setErr(null);
    const tempId = 'tmp-' + Date.now();
    const quoted = replyTo;
    const optimistic = { id: tempId, sender_email: email, body, kind: 'text', created_at: new Date().toISOString(), pending: true,
      ...(quoted ? { reply_to: quoted.id, reply: { id: quoted.id, sender_email: quoted.sender_email, body: String(quoted.body || '').slice(0, 140), kind: quoted.kind } } : {}) };
    setMsgs(cur => [...(cur || []), optimistic]);
    setText(""); setReplyTo(null);
    try {
      const payload = { body, ...(quoted ? { reply_to: quoted.id } : {}) };
      const r = await orgApi('send_message', threadId ? { thread_id: threadId, ...payload } : { to_email: who?.email, ...payload });
      if (!threadId && r?.thread_id) setThreadId(r.thread_id);
      setMsgs(cur => (cur || []).map(m => m.id === tempId ? { ...optimistic, ...(r?.message || {}), pending: false } : m));
    } catch (e) {
      const er = orgErr(e);
      setMsgs(cur => (cur || []).map(m => m.id === tempId ? { ...m, pending: false, failed: true } : m));
      setErr(er); setText(body); if (quoted && er.code !== 'invalid_reply_to') setReplyTo(quoted);
    }
    setSending(false);
  };

  // ─── Media: every voice note / video / form check is a temp message with an upload job ───
  const patchTemp = (id, fn) => setMsgs(cur => (cur || []).map(m => m.id === id ? fn(m) : m));
  const runJob = async (tempId) => {
    const job = jobs.current[tempId];
    if (!job || job.running) return;
    job.running = true;
    if (!job.thread_id && threadRef.current) job.thread_id = threadRef.current;
    patchTemp(tempId, m => ({ ...m, upload: { stage: job.stage, progress: 0, error: null } }));
    try {
      const r = await runMediaJob(job, {
        onProgress: (p) => patchTemp(tempId, m => ({ ...m, upload: { ...(m.upload || {}), progress: p } })),
        onStage: (st) => patchTemp(tempId, m => ({ ...m, upload: { ...(m.upload || {}), stage: st } })),
      });
      if (!threadRef.current && (r?.thread_id || job.thread_id)) setThreadId(r?.thread_id || job.thread_id);
      delete jobs.current[tempId];
      if (job.localUrl) { try { URL.revokeObjectURL(job.localUrl); } catch {} }
      if (r?.message) {
        const now = Date.now();
        setMsgs(cur => (cur || []).map(m => m.id === tempId
          ? { ...r.message, reply: m.reply, pending: false, media: r.message.media ? { ...r.message.media, _fetched_at: r.message.media.url ? now : null } : null }
          : m));
      } else {
        setMsgs(cur => (cur || []).filter(m => m.id !== tempId));
        const tid = threadRef.current || job.thread_id;
        if (tid) { try { const { list } = await fetchMessages(tid); setMsgs(cur => merge(cur, list)); } catch {} }
      }
    } catch (er) {
      job.running = false;
      patchTemp(tempId, m => ({ ...m, upload: { ...(m.upload || {}), error: mediaErrorText(er.code, er) } }));
    }
  };
  const discardJob = (m) => {
    const job = jobs.current[m.id];
    if (job?.localUrl) { try { URL.revokeObjectURL(job.localUrl); } catch {} }
    delete jobs.current[m.id];
    setMsgs(cur => (cur || []).filter(x => x.id !== m.id));
  };
  // blob + base type + duration → a checked job, a temp message, and the upload started.
  const queueMedia = ({ blob, type, duration, purpose = 'message', exercise = '', caption = '' }) => {
    const bytes = blob.size;
    const check = mediaSpec({ content_type: type, bytes, duration_sec: duration || null, purpose, exercise });
    if (check.error) { setMediaNote(mediaErrorText(check.error, check.detail)); return false; }
    const { spec } = check;
    const tempId = `tmp-media-${Date.now()}-${++_tmpSeq}`;
    const localUrl = URL.createObjectURL(blob);
    const quoted = replyTo;
    // An exact-size, exact-type body for the PUT
    const body = blob.type === spec.content_type ? blob : new Blob([blob], { type: spec.content_type });
    jobs.current[tempId] = { blob: body, content_type: spec.content_type, bytes, duration_sec: spec.duration_sec, purpose: spec.purpose, exercise: spec.exercise,
      caption: caption || '', reply_to: quoted ? quoted.id : null, thread_id: threadRef.current, to_email: who?.email, stage: 'begin', localUrl };
    const media = { id: tempId, kind: spec.kind, content_type: spec.content_type, bytes, duration_sec: spec.duration_sec, purpose: spec.purpose, exercise: spec.exercise, url: localUrl, _local: true };
    setMsgs(cur => [...(cur || []), { id: tempId, sender_email: email, kind: 'media', body: mediaLabel(media, caption), created_at: new Date().toISOString(), pending: true, media,
      upload: { stage: 'begin', progress: 0, error: null },
      ...(quoted ? { reply_to: quoted.id, reply: { id: quoted.id, sender_email: quoted.sender_email, body: String(quoted.body || '').slice(0, 140), kind: quoted.kind } } : {}) }]);
    setReplyTo(null); setMediaNote(null);
    runJob(tempId);
    return true;
  };

  const onVideoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    setAttachOpen(false);
    if (!file) return;
    const r = await checkVideoFile(file, 'message');
    if (r.error) { setMediaNote(r.error); return; }
    // Text already typed goes along as the caption
    const caption = text.trim().slice(0, MAX_LEN);
    if (queueMedia({ blob: file, type: r.type, duration: r.duration, caption })) setText('');
  };
  const sendVoice = () => {
    const res = rec.result;
    if (!res) return;
    if (queueMedia({ blob: res.blob, type: res.type, duration: res.duration })) rec.clear();
  };
  const sendFormCheck = async ({ file, type, duration, exercise, caption }) => {
    if (queueMedia({ blob: file, type, duration, purpose: 'form_check', exercise, caption })) setFormCheck(false);
  };

  const recording = rec.state === 'recording' || rec.state === 'starting';
  const recorded = rec.state === 'recorded' && rec.result;
  const quote = replyTo ? replyPreview({ id: replyTo.id, sender_email: replyTo.sender_email, body: replyTo.body, kind: replyTo.kind }, email, otherName) : null;
  const roundBtn = (active) => ({ width: 44, height: 44, borderRadius: 14, border: "none", flexShrink: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    background: active ? `linear-gradient(135deg,${V.accent},${V.accent2})` : V.hoverBg, WebkitTapHighlightColor: "transparent" });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "var(--content-h, 70vh)", minHeight: 320 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 10, borderBottom: `1px solid ${V.divider}`, flexShrink: 0 }}>
        <button onClick={onBack} aria-label="Back to inbox" style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 4, cursor: "pointer", paddingTop: 6, paddingBottom: 6, paddingLeft: 0, paddingRight: 4, WebkitTapHighlightColor: "transparent" }}>
          {Icons.chevLeft({ size: 18, color: V.accent })}<span style={{ fontSize: 14, color: V.accent, fontWeight: 600 }}>Inbox</span>
        </button>
        <Avatar who={who} size={32} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: V.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{otherName}</div>
          <div style={{ fontSize: 10, color: V.text3 }}>{readOnly ? 'No longer your coach · read only' : 'Your coach'}</div>
        </div>
      </div>

      <div ref={listRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingTop: 12, paddingBottom: 8, display: "flex", flexDirection: "column", gap: 8 }}>
        {nextBefore && <div style={{ textAlign: "center" }}><LinkBtn color={V.text3} disabled={loadingMore} onClick={loadMore}>{loadingMore ? 'Loading…' : 'Load earlier messages'}</LinkBtn></div>}
        {msgs === null && <Skeleton rows={4} />}
        {msgs?.length === 0 && <div style={{ textAlign: "center", fontSize: 12, color: V.text3, paddingTop: 24 }}>No messages yet — say hi.</div>}
        {(msgs || []).map(m => <Bubble key={m.id} m={m} me={email} otherName={otherName} mine={m.sender_email === email} onAllow={allow}
          onReply={readOnly ? null : startReply} onJump={jumpTo} onMediaError={onMediaError} onRetry={(x) => runJob(x.id)} onDiscard={discardJob} />)}
        <div ref={bottomRef} />
      </div>

      {err && <Notice tone="error" style={{ paddingTop: 4, paddingBottom: 4 }}>{errorMessage(err)}</Notice>}
      {readOnly ? (
        <div style={{ fontSize: 11, color: V.text3, textAlign: "center", paddingTop: 10, paddingBottom: 6 }}>This conversation is read only.</div>
      ) : (<>
        {(mediaNote || rec.error) && <Notice tone="error" style={{ paddingTop: 4, paddingBottom: 4 }}>{mediaNote || rec.error}</Notice>}
        {quote && (
          <div data-testid="reply-bar" style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8, paddingBottom: 6, borderTop: `1px solid ${V.divider}` }}>
            {Icons.reply({ size: 15, color: V.accent })}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: V.accent }}>Replying to {quote.who === 'You' ? 'yourself' : quote.who}</div>
              <div style={{ fontSize: 12, color: V.text3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{quote.text}</div>
            </div>
            <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply" style={{ background: "none", border: "none", cursor: "pointer", paddingTop: 6, paddingBottom: 6, paddingLeft: 6, paddingRight: 6 }}>
              {Icons.x({ size: 16, color: V.text3 })}</button>
          </div>
        )}
        {attachOpen && !recording && !recorded && (
          <div id="inbox-attach" role="group" aria-label="Attach" data-testid="attach-menu" style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 8 }}>
            <button type="button" onClick={() => recordInput.current?.click()} style={chipBtn}>{Icons.video({ size: 14, color: V.accent })} Record a video</button>
            <button type="button" onClick={() => chooseInput.current?.click()} style={chipBtn}>{Icons.upload({ size: 14, color: V.accent })} Choose a video</button>
            <button type="button" onClick={() => { setAttachOpen(false); setFormCheck(true); }} style={chipBtn}>{Icons.dumbbell({ size: 14, color: V.accent })} Send a form check</button>
          </div>
        )}
        <input ref={recordInput} type="file" accept="video/*" capture="environment" onChange={onVideoFile} style={{ display: "none" }} data-testid="video-record-input" />
        <input ref={chooseInput} type="file" accept="video/*" onChange={onVideoFile} style={{ display: "none" }} data-testid="video-choose-input" aria-label="Choose a video" />
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingTop: 10, borderTop: quote || attachOpen ? "none" : `1px solid ${V.divider}`, flexShrink: 0 }}>
          {recording ? (<>
            <button type="button" onClick={rec.cancel} aria-label="Cancel recording" style={roundBtn(false)}>{Icons.x({ size: 18, color: V.text3 })}</button>
            <div role="timer" aria-live="off" data-testid="rec-timer" style={{ flex: 1, minHeight: 44, display: "flex", alignItems: "center", gap: 8, paddingLeft: 14, paddingRight: 14, borderRadius: 14, background: V.inputBg, border: `1px solid ${V.danger}55` }}>
              <span aria-hidden style={{ width: 10, height: 10, borderRadius: 5, background: V.danger, animation: "pulse 1s ease-in-out infinite" }} />
              <span style={{ fontSize: 14, fontFamily: V.mono, color: V.text, fontWeight: 700 }}>{fmtDuration(rec.elapsed)}</span>
              <span style={{ fontSize: 12, fontFamily: V.mono, color: V.text3 }}>/ {fmtDuration(RECORD_MAX_SEC)}</span>
              <span style={{ fontSize: 12, color: V.text3, marginLeft: "auto" }}>{rec.state === 'starting' ? 'Starting…' : 'Recording'}</span>
            </div>
            <button type="button" onClick={rec.stop} disabled={rec.state !== 'recording'} aria-label="Stop recording" style={roundBtn(true)}>{Icons.stop({ size: 16, color: V.onAccent })}</button>
          </>) : recorded ? (<>
            <button type="button" onClick={rec.clear} aria-label="Discard voice note" style={roundBtn(false)}>{Icons.trash({ size: 17, color: V.text3 })}</button>
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <audio controls src={rec.result.url} preload="metadata" aria-label="Your voice note" style={{ flex: 1, minWidth: 0, height: 40 }} />
              <span style={{ fontSize: 12, fontFamily: V.mono, color: V.text3, flexShrink: 0 }}>{fmtDuration(rec.result.duration)}</span>
            </div>
            <button type="button" onClick={sendVoice} aria-label="Send voice note" style={roundBtn(true)}>{Icons.arrowUp({ size: 18, color: V.onAccent, strokeWidth: 2.6 })}</button>
          </>) : (<>
            <button type="button" onClick={() => { setAttachOpen(o => !o); setMediaNote(null); }} aria-label="Attach video or form check" aria-expanded={attachOpen} aria-controls="inbox-attach"
              style={roundBtn(false)}>{attachOpen ? Icons.x({ size: 18, color: V.text2 }) : Icons.plus({ size: 20, color: V.text2 })}</button>
            <textarea ref={textRef} value={text} onChange={e => setText(e.target.value.slice(0, MAX_LEN))} placeholder={`Message ${otherName}`} rows={1}
              aria-label="Message" maxLength={MAX_LEN} data-no-swipe
              style={{ flex: 1, minHeight: 44, maxHeight: 120, resize: "none", background: V.inputBg, border: `1px solid ${V.cardBorder}`, borderRadius: 14,
                paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, color: V.text, fontSize: 16, fontFamily: V.font, outline: "none", boxSizing: "border-box", lineHeight: 1.35 }} />
            {!text.trim() && canRecord ? (
              <button type="button" onClick={() => { setAttachOpen(false); setMediaNote(null); rec.start(); }} aria-label="Record a voice note" style={roundBtn(false)}>
                {Icons.mic({ size: 19, color: V.accent })}</button>
            ) : (
              <button type="button" onClick={send} disabled={!text.trim() || sending} aria-label="Send"
                style={{ ...roundBtn(!!text.trim()), cursor: text.trim() && !sending ? "pointer" : "default", opacity: sending ? .6 : 1 }}>
                {Icons.arrowUp({ size: 18, color: text.trim() ? V.onAccent : V.text3, strokeWidth: 2.6 })}
              </button>
            )}
          </>)}
        </div>
        {!recording && !recorded && <div style={{ fontSize: 10, color: V.text3, textAlign: "right", paddingTop: 4, fontFamily: V.mono }}>{text.length}/{MAX_LEN}</div>}
      </>)}
      {formCheck && <FormCheckSheet exerciseNames={exerciseNames} onClose={() => setFormCheck(false)} onSend={sendFormCheck} />}
    </div>
  );

  async function allow(m) {
    const scope = String(m.body || '').trim();
    try {
      await orgApi('set_consent', { [`share_${scope}`]: true, source: 'coach_request' });
      setMsgs(cur => (cur || []).map(x => x.id === m.id ? { ...x, granted: true } : x));
      SuccessToastCtrl.show(`Now sharing ${shortScope(scope)}`);
    } catch (e) { setErr(orgErr(e)); }
  }
}

const chipBtn = { display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36, paddingTop: 7, paddingBottom: 7, paddingLeft: 12, paddingRight: 12, borderRadius: 18,
  background: V.inputBg, border: `1px solid ${V.cardBorder}`, color: V.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: V.font, WebkitTapHighlightColor: "transparent" };

function merge(cur, incoming) {
  const now = Date.now();
  const map = new Map();
  for (const m of cur || []) map.set(String(m.id), m);
  for (const m of incoming || []) {
    const prev = map.get(String(m.id));
    const next = { ...(prev || {}), ...m, pending: false };
    // Keep a still-valid signed link so a playing clip is not reloaded by every poll
    if (m.media) next.media = mergeMedia(prev && prev.media, m.media, now);
    map.set(String(m.id), next);
  }
  // Drop optimistic text temps that the server has since echoed back (same sender + body).
  // Media temps are replaced by their upload result, never matched by body.
  const list = [...map.values()].filter(m => !(String(m.id).startsWith('tmp-') && !m.upload && (incoming || []).some(x => x.body === m.body && x.sender_email === m.sender_email)));
  return list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || (Number(a.id) || 0) - (Number(b.id) || 0));
}

function Bubble({ m, mine, me, otherName, onAllow, onReply, onJump, onMediaError, onRetry, onDiscard }) {
  const press = useRef(null);
  if (m.kind === 'consent_request') {
    const scope = String(m.body || '').trim();
    return (
      <div style={{ alignSelf: "stretch", borderRadius: 14, border: `1px solid ${V.accent}30`, background: `${V.accent}0d`, paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <IconTile icon={Icons.users} size={30} iconSize={14} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: V.text }}>Your coach asked to see your {shortScope(scope)}</div>
            <div style={{ fontSize: 11, color: V.text3, marginTop: 2 }}>{fmtWhen(m.created_at)} · You can turn this off any time in Settings</div>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          {m.granted ? <Notice tone="ok">Shared</Notice> : <Btn v="small" onClick={() => onAllow(m)}>Allow</Btn>}
        </div>
      </div>
    );
  }
  if (m.kind === 'system') {
    return <div style={{ alignSelf: "center", fontSize: 11, color: V.text3, textAlign: "center", paddingTop: 4, paddingBottom: 4, maxWidth: "85%" }}>{m.body}</div>;
  }
  const replyable = !!onReply && canReplyTo(m);
  // Long-press (touch) quotes the message; the reply button does the same for everyone else.
  const startPress = (e) => {
    if (!replyable || (e.pointerType === 'mouse' && e.button !== 0)) return;
    if (e.target.closest && e.target.closest('audio,video,button,a')) return;
    clearTimeout(press.current);
    press.current = setTimeout(() => { press.current = null; onReply(m); }, 500);
  };
  const endPress = () => { clearTimeout(press.current); press.current = null; };
  const isMedia = m.kind === 'media' && m.media;
  const status = m.failed ? 'Not sent — tap Send to retry' : m.upload ? '' : m.pending ? 'Sending…' : fmtWhen(m.created_at);
  return (
    <div data-msg-id={m.id} data-testid="org-message" style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "86%", display: "flex", alignItems: "center", gap: 4, flexDirection: mine ? "row-reverse" : "row" }}>
      <div style={{ minWidth: 0 }}>
        <div onPointerDown={startPress} onPointerUp={endPress} onPointerLeave={endPress} onPointerCancel={endPress} onPointerMove={(e) => { if (Math.abs(e.movementY) > 4) endPress(); }}
          onContextMenu={(e) => { if (replyable && e.nativeEvent && e.nativeEvent.pointerType === 'touch') e.preventDefault(); }}
          style={{ borderRadius: 16, borderBottomRightRadius: mine ? 5 : 16, borderBottomLeftRadius: mine ? 16 : 5,
          background: mine ? `linear-gradient(135deg,${V.accent},${V.accent2})` : V.cardGrad, border: mine ? "none" : `1px solid ${V.cardBorder}`,
          color: mine ? V.onAccent : V.text, paddingTop: 9, paddingBottom: 9, paddingLeft: 13, paddingRight: 13, fontSize: 14, lineHeight: 1.45,
          whiteSpace: "pre-wrap", wordBreak: "break-word", opacity: m.pending && !m.upload ? .6 : 1, WebkitTouchCallout: "none" }}>
          {m.reply_to ? <ReplyQuote reply={m.reply || null} me={me} otherName={otherName} mine={mine} onJump={onJump} /> : null}
          {isMedia ? <MediaContent m={m} mine={mine} onMediaError={onMediaError} onRetry={onRetry} onDiscard={onDiscard} /> : m.body}
        </div>
        {status && <div style={{ fontSize: 9, color: m.failed ? V.danger : V.text3, marginTop: 3, textAlign: mine ? "right" : "left", fontFamily: V.mono }}>{status}</div>}
      </div>
      {replyable && (
        <button type="button" onClick={() => onReply(m)} aria-label={`Reply to ${mine ? 'your message' : otherName}: ${String(m.body || '').slice(0, 60)}`} data-testid="reply-btn"
          style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 15, border: "none", background: "transparent", cursor: "pointer", opacity: .55,
            display: "flex", alignItems: "center", justifyContent: "center", WebkitTapHighlightColor: "transparent", paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}>
          {Icons.reply({ size: 15, color: V.text3 })}
        </button>
      )}
    </div>
  );
}
